import Auth from '@aws-amplify/auth';
import { Hub, HubCapsule } from '@aws-amplify/core';
import React, {
  createContext, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import awsExportsOverriden from '../aws-exports-overriden';
import LoadingPage from './Loading/Page';

Auth.configure(awsExportsOverriden);

type AuthenticationProps = {
  children: JSX.Element | JSX.Element[],
};

export type User = {
  github: string,
  username: string,
  email?: string,
  emailVerified?: boolean,
  name?: string,
  firstName?: string,
  lastName?: string,
  picture?: string,
  profile?: string,
  identityId: string,
};

export type AuthContextValue = {
  user?: User,
  cognitoUser?: any,
  isSignedIn: boolean,
  refreshUser: () => void,
  signOut: Function,
};

const INITIAL_CONTEXT_VALUE = { isSignedIn: false, signOut: () => {}, refreshUser: () => {} };

export const AuthContext = createContext<AuthContextValue>(INITIAL_CONTEXT_VALUE);

function contextValueFrom(userData: any, getUser: () => void) : AuthContextValue {
  const attributes = userData?.attributes;
  if (!attributes) return INITIAL_CONTEXT_VALUE;
  return {
    isSignedIn: true,
    user: {
      identityId: userData.identityId,
      github: attributes['custom:githubUsername'],
      username: userData.username,
      email: attributes['custom:contactEmail'] || attributes.email,
      emailVerified: attributes.email_verified,
      name: attributes.name,
      firstName: attributes['custom:firstName'],
      lastName: attributes['custom:lastName'],
      picture: attributes.picture,
      profile: attributes.profile,
    },
    signOut() {
      return Auth.signOut();
    },
    refreshUser() {
      return getUser();
    },
    cognitoUser: userData,
  };
}

async function getCurrentUser() {
  try {
    const user = await Auth.currentAuthenticatedUser();
    if (user) {
      const credentials = await Auth.currentUserCredentials();
      user.identityId = credentials.identityId;
    }
    return user;
  } catch (err) {
    return null;
  }
}

export function useAuth() {
  return useContext(AuthContext);
}

export default function Authentication({ children } : AuthenticationProps) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentGetUserPromise = useRef<Promise<any> | null>(null);
  function singleGetUser() {
    const currentPromise = currentGetUserPromise.current ?? Promise.resolve();
    currentGetUserPromise.current = currentPromise.then(getCurrentUser);
    return currentGetUserPromise.current.then(setUserData);
  }
  const history = useHistory();
  useEffect(() => {
    function listener(capsule: HubCapsule) {
      const { payload: { event, data } } = capsule;
      switch (event) {
        case 'signIn':
        // case 'cognitoHostedUI':
          singleGetUser();
          break;
        case 'signOut':
          setUserData(null);
          break;
        case 'customOAuthState': {
          const state = JSON.parse(data);
          if (state.returnTo) {
            if (currentGetUserPromise.current) {
              currentGetUserPromise.current.then(() => {
                history.replace(state.returnTo);
              });
            }
          }
          break;
        }
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
          break;
        default:
          break;
      }
    }
    Hub.listen('auth', listener);
    singleGetUser().then(() => setLoading(false));
    return () => Hub.remove('auth', listener);
  }, [history]);
  const value = useMemo(() => contextValueFrom(userData, singleGetUser), [userData]);
  if (loading) return <LoadingPage />;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
